import {useCallback, useEffect, useMemo, useState, type ReactNode} from 'react';
import {useIntl} from 'react-intl';
import {useMediaDevices, usePreviewTracks} from '@livekit/components-react';
import {Track, type LocalVideoTrack} from 'livekit-client';
import {PreviewVideo} from './PreviewVideo';
import type {CallIntent} from '@phonehq/rtc-core';
import {loadDevicePreferences} from '../../groupCalls/services/devicePreferences';
import {toDeviceOptions} from '../../groupCalls/services/mediaDevices';
import {classifyMediaError, probeDeviceIssues, requestDevices, type DeviceIssues} from '../../groupCalls/services/deviceAccess';
import type {MediaDevicePreferences} from '../../groupCalls/platform/WebRtcPlatform';
import type {PrepDeviceField} from './PrepDialog';
import {prepMessages as m} from './prepMessages';

export interface PrepMedia {
    readonly microphone: PrepDeviceField;
    readonly camera?: PrepDeviceField;
    readonly preview?: ReactNode;
    readonly issues: DeviceIssues;
    readonly devices: MediaDevicePreferences;
    readonly microphoneEnabled: boolean;
    readonly cameraEnabled: boolean;
    readonly retryMicrophone: () => void;
}

export const usePrepMedia = (intent: CallIntent, videoAvailable: boolean): PrepMedia => {
    const intl = useIntl();
    const [devices, setDevices] = useState<MediaDevicePreferences>(loadDevicePreferences);
    const [microphoneOn, setMicrophoneOn] = useState(true);
    const [cameraOn, setCameraOn] = useState(true);
    const [issues, setIssues] = useState<DeviceIssues>({});
    const video = videoAvailable && intent === 'video';
    const inputs = useMediaDevices({kind: 'audioinput'});
    const cameras = useMediaDevices({kind: 'videoinput'});

    useEffect(() => {
        let active = true;
        void probeDeviceIssues(['microphone']).then(found => {
            if (active && found.microphone) {
                setIssues(current => ({...current, microphone: found.microphone}));
            }
        });
        return () => {
            active = false;
        };
    }, []);

    const retryMicrophone = useCallback(() => {
        void requestDevices(['microphone']).then(found => setIssues(current => ({...current, microphone: found.microphone})));
    }, []);

    const onPreviewError = useCallback((error: Error) => {
        setIssues(current => ({...current, camera: classifyMediaError(error)}));
    }, []);
    const previewOptions = useMemo(() => ({
        audio: false as const,
        video: video && cameraOn && !issues.camera ? (devices.videoInputId ? {deviceId: devices.videoInputId} : true) : false,
    }), [cameraOn, devices.videoInputId, issues.camera, video]);
    const tracks = usePreviewTracks(previewOptions, onPreviewError);
    const videoTrack = tracks?.find(track => track.kind === Track.Kind.Video) as LocalVideoTrack | undefined;

    const defaultOption = {deviceId: '', label: intl.formatMessage(m.systemDefault)};
    const microphone: PrepDeviceField = {
        enabled: microphoneOn && !issues.microphone,
        onToggle: () => (issues.microphone ? retryMicrophone() : setMicrophoneOn(current => !current)),
        devices: [defaultOption, ...toDeviceOptions(inputs, intl.formatMessage(m.microphone))],
        value: devices.audioInputId ?? '',
        onChange: deviceId => setDevices(current => ({...current, audioInputId: deviceId || undefined})),
        unavailableLabel: issues.microphone ? intl.formatMessage(m.microphoneBlockedDevice) : undefined,
        testId: 'group-call-lobby-mic',
    };
    const cameraOptions = toDeviceOptions(cameras, intl.formatMessage(m.camera));
    const noCamera = issues.camera === 'unavailable';
    const camera: PrepDeviceField | undefined = video ? {
        enabled: cameraOn && !issues.camera,
        onToggle: () => {
            setIssues(current => ({...current, camera: undefined}));
            setCameraOn(current => !current);
        },
        devices: [defaultOption, ...cameraOptions],
        value: devices.videoInputId ?? '',
        onChange: deviceId => setDevices(current => ({...current, videoInputId: deviceId || undefined})),
        unavailableLabel: noCamera ? intl.formatMessage(m.noCamera) : undefined,
        testId: 'group-call-lobby-camera',
    } : undefined;

    return {
        microphone,
        camera,
        preview: video && videoTrack ? <PreviewVideo track={videoTrack}/> : undefined,
        issues,
        devices,
        microphoneEnabled: microphoneOn && !issues.microphone,
        cameraEnabled: video && cameraOn && !issues.camera,
        retryMicrophone,
    };
};
